import {Injector, NgModule} from "@angular/core";
import {RouterModule, Routes} from "@angular/router";
import {CookieModule} from "ngx-cookie";
import {BrowserModule} from "@angular/platform-browser";
import {CommonModule, NgOptimizedImage} from "@angular/common";
import {FormsModule} from "@angular/forms";
import {provideHttpClient, withInterceptorsFromDi} from "@angular/common/http";
import {TypeaheadWindowComponent} from "../common/typeahead/typeahead-window.component";
import {PopoverComponent} from "../common/popover/popover.component";
import {PortVisitCommonModule} from "../common/port-visit-common.module";
import {newRouteGuard} from "./new-route.guard";
import {commercialReleaseRedirectGuard} from "../commercial-release/commercial-release-redirect.guard";
import {RouteData} from "../route-data";
import {HighlightComponent} from "../common/typeahead/highlight/highlight.component";
import {ConsignmentsOverviewComponent} from './consignments/consignments-overview/consignments-overview.component';
import {
  MasterConsignmentOverviewItemComponent
} from './consignments/consignments-overview/consignment-overview-item/master-consignment-overview-item.component';
import {
  ConsignmentEquipmentOverviewItemComponent
} from './consignments/consignments-overview/consignment-overview-item/consignment-equipment-overview-item/consignment-equipment-overview-item.component';
import {
  MasterConsignmentDetailsComponent
} from './consignments/details/master-consignment-details/master-consignment-details.component';
import {
  MasterConsignmentRouteComponent
} from './consignments/details/master-consignment-details/master-consignment-route/master-consignment-route.component';
import {
  ConsignmentPartyDetailsComponent
} from './consignments/details/master-consignment-details/consignment-party-details/consignment-party-details.component';
import {
  HouseConsignmentDetailsComponent
} from './consignments/details/house-consignment-details/house-consignment-details.component';
import {
  EquipmentConsignmentDetailsComponent
} from './consignments/details/equipment-consignment-details/equipment-consignment-details.component';
import {
  GoodsConsignmentDetailsComponent
} from './consignments/details/goods-consignment-details/goods-consignment-details.component';
import {GoodsPackagingComponent} from './consignments/details/goods-packaging/goods-packaging.component';
import {
  ConsignmentSupplementaryDeclarantComponent
} from './consignments/details/master-consignment-details/consignment-supplementary-declarant/consignment-supplementary-declarant.component';
import {
  AdditionalSupplyChainActorDetailsComponent
} from './consignments/details/master-consignment-details/additional-supply-chain-actor-details/additional-supply-chain-actor-details.component';
import {ContainersOverviewComponent} from "./containers/containers-overview/containers-overview.component";
import {
  ContainerOverviewItemComponent
} from "./containers/containers-overview/consignment-overview-item/container-overview-item.component";
import {
  ContainerConsignmentOverviewItemComponent
} from "./containers/containers-overview/consignment-overview-item/container-consignment-overview-item/container-consignment-overview-item.component";
import {
  ConsignmentGoodsTableComponent
} from "./consignments/details/tables/consignment-goods-table/consignment-goods-table.component";
import {
  ConsignmentEquipmentsTableComponent
} from "./consignments/details/tables/consignment-equipments-table/consignment-equipments-table.component";
import {
  HouseConsignmentsTableComponent
} from "./consignments/details/tables/house-consignments-table/house-consignments-table.component";
import {
  ConsignmentEquipmentsLinkTableComponent
} from "./consignments/details/tables/consignment-equipments-link-table/consignment-equipments-link-table.component";
import {
  ConsignmentGoodsColumnComponent
} from './consignments/details/tables/consignment-goods-column/consignment-goods-column.component';
import {
  ConsignmentHouseColumnComponent
} from './consignments/details/tables/consignment-house-column/consignment-house-column.component';
import {
  ConsignmentVehiclesTableComponent
} from './consignments/details/tables/consignment-vehicles-table/consignment-vehicles-table.component';
import {
  ConsignmentBulkGoodsOverviewItemComponent
} from './consignments/consignments-overview/consignment-overview-item/consignment-bulk-goods-overview-item/consignment-bulk-goods-overview-item.component';
import {
  ConsignmentCarrierDetailsComponent
} from "./consignments/details/master-consignment-details/consignment-carrier-details/consignment-carrier-details.component";
import {
  ConsignmentHistoryDetailsComponent
} from './consignments/details/consignment-history-details/consignment-history-details.component';
import {EstimatedActualFieldComponent} from './common/estimated-actual-field/estimated-actual-field.component';
import {
  ConsignmentDetailsAlertsComponent
} from './consignments/details/consignment-details-alerts/consignment-details-alerts.component';
import {BunkeringComponent} from "../bunkering/bunkering.component";
import {BunkeringDeclarationComponent} from "../bunkering/bunkering-declaration/bunkering-declaration.component";
import {
  SelectionBunkeringBerthVisitsComponent
} from "../bunkering/edit/visit-selection/selection-bunkering-berth-visits.component";
import {SelectionBunkerLicenseComponent} from "../bunkering/edit/license-selection/selection-bunker-license.component";
import {EditBunkeringComponent} from "../bunkering/edit/edit-bunkering.component";
import {FuelExemptions} from "../bunkering/fuel-exemptions/fuel-exemptions";
import {EditFuelExemption} from "../bunkering/fuel-exemptions/edit/edit-fuel-exemption";
import {ConsignmentHandler} from "./handlers/consignment-handler";
import {InjectorProvider} from "../common/injector-provider";
import {AppHandler} from "./handlers/app-handler";
import {
  ConsignmentOrganisationPartyDetailsComponent
} from './consignments/details/master-consignment-details/consignment-organisation-party-details/consignment-organisation-party-details.component';
import {NavigationToggleComponent} from './common/navigation-toggle/navigation-toggle.component';
import {OverviewNavigationTabsComponent} from "./common/overview-navigation-tabs/overview-navigation-tabs.component";
import {
  StevedoreVisitOverviewServiceStatusComponent
} from "./visit-overview/visit-overview-item/visit-overview-service-status/stevedore-visit-overview-service-status/stevedore-visit-overview-service-status.component";
import {
  DeclareTranshipmentContainerConsignmentComponent
} from "./consignments/declare-transhipment/container-consignment/declare-transhipment-container-consignment.component";
import {
  DeclareTranshipmentBulkConsignmentComponent
} from "./consignments/declare-transhipment/bulk-consignment/declare-transhipment-bulk-consignment.component";
import {MovementIconsComponent} from "./common/movement-icons/movement-icons.component";
import {
  FacetFilterSearchFieldComponent
} from "./common/facets/facet-filter/facet-filter-search-field/facet-filter-search-field.component";
import {
  OverviewNavigationTabItemComponent
} from "./common/overview-navigation-tabs/overview-navigation-tab-item/overview-navigation-tab-item.component";
import {
  HarbourDuesOverviewItemComponent
} from "./harbour-dues-rotterdam-overview/harbour-dues-overview-item/harbour-dues-overview-item.component";
import {HarbourDuesOverviewComponent} from "./harbour-dues-rotterdam-overview/harbour-dues-overview.component";
import {CommercialReleaseOverviewComponent} from "./commercial-release/commercial-release-overview.component";
import {
  CommercialReleaseOverviewItemComponent
} from "./commercial-release/commercial-release-overview-item/commercial-release-overview-item.component";
import {
  CommercialReleaseModalComponent
} from "./commercial-release/commercial-release-modal/commercial-release-modal.component";
import {
  CommercialReleaseModalDetailsComponent
} from "./commercial-release/commercial-release-modal/commercial-release-modal-details/commercial-release-modal-details.component";
import {
  CommercialReleaseModalHistoryComponent
} from "./commercial-release/commercial-release-modal/commercial-release-modal-history/commercial-release-modal-history.component";
import {
  CommercialReleaseModalPartiesComponent
} from "./commercial-release/commercial-release-modal/commercial-release-modal-parties/commercial-release-modal-parties.component";
import {
  CommercialReleasePartyFieldComponent
} from "./commercial-release/commercial-release-modal/commercial-release-modal-parties/commercial-release-party-field/commercial-release-party-field.component";
import {
  CommercialReleaseUploadModalComponent
} from "./commercial-release/commercial-release-upload-modal/commercial-release-upload-modal.component";

const appRoutes: Routes = [
  {
    path: 'port-dues',
    canActivate: [newRouteGuard],
    component: HarbourDuesOverviewComponent,
    data: <RouteData>{
      manualScrolling: true
    }
  },
  {
    path: 'cargo',
    canActivate: [newRouteGuard],
    component: ConsignmentsOverviewComponent,
    data: <RouteData>{
      manualScrolling: true
    }
  },
  {
    path: 'cargo/:crn',
    canActivate: [newRouteGuard],
    component: ConsignmentsOverviewComponent,
    data: <RouteData>{
      manualScrolling: true
    }
  },
  {
    path: 'cargo/:crn/:cargoDeclarantId',
    canActivate: [newRouteGuard],
    component: ConsignmentsOverviewComponent,
    data: <RouteData>{
      manualScrolling: true
    }
  },
  {
    path: 'container',
    canActivate: [newRouteGuard],
    component: ContainersOverviewComponent,
    data: <RouteData>{
      manualScrolling: true
    }
  },
  {
    path: 'container/:crn',
    canActivate: [newRouteGuard],
    component: ContainersOverviewComponent,
    data: <RouteData>{
      manualScrolling: true
    }
  },
  {
    path: 'bunkering',
    canActivate: [newRouteGuard],
    component: BunkeringComponent,
    data: <RouteData>{
      manualScrolling: true
    }
  },
  {
    path: 'commercial-release-new',
    canActivate: [newRouteGuard, commercialReleaseRedirectGuard],
    component: CommercialReleaseOverviewComponent,
    data: <RouteData>{
      manualScrolling: true
    }
  },
  {
    path: 'commercial-release-new/:crn',
    canActivate: [newRouteGuard, commercialReleaseRedirectGuard],
    component: CommercialReleaseOverviewComponent,
    data: <RouteData>{
      manualScrolling: true
    }
  }
];

@NgModule({
  declarations: [
    // Components
    HarbourDuesOverviewItemComponent,
    HarbourDuesOverviewComponent,
    ConsignmentsOverviewComponent,
    MasterConsignmentOverviewItemComponent,
    ConsignmentEquipmentOverviewItemComponent,
    MasterConsignmentDetailsComponent,
    MasterConsignmentRouteComponent,
    ConsignmentPartyDetailsComponent,
    ConsignmentCarrierDetailsComponent,
    HouseConsignmentDetailsComponent,
    EquipmentConsignmentDetailsComponent,
    GoodsConsignmentDetailsComponent,
    ConsignmentGoodsTableComponent,
    ConsignmentEquipmentsTableComponent,
    GoodsPackagingComponent,
    ContainersOverviewComponent,
    ContainerOverviewItemComponent,
    ContainerConsignmentOverviewItemComponent,
    // Bunkering
    BunkeringComponent,
    BunkeringDeclarationComponent,
    SelectionBunkeringBerthVisitsComponent,
    SelectionBunkerLicenseComponent,
    EditBunkeringComponent,
    FuelExemptions,
    EditFuelExemption,
    ConsignmentSupplementaryDeclarantComponent,
    AdditionalSupplyChainActorDetailsComponent,
    HouseConsignmentsTableComponent,
    ConsignmentEquipmentsLinkTableComponent,
    ConsignmentGoodsColumnComponent,
    ConsignmentHouseColumnComponent,
    ConsignmentVehiclesTableComponent,
    ConsignmentBulkGoodsOverviewItemComponent,
    ConsignmentHistoryDetailsComponent,
    EstimatedActualFieldComponent,
    ConsignmentDetailsAlertsComponent,
    ConsignmentOrganisationPartyDetailsComponent,
    OverviewNavigationTabsComponent,
    OverviewNavigationTabItemComponent,
    NavigationToggleComponent,
    DeclareTranshipmentContainerConsignmentComponent,
    DeclareTranshipmentBulkConsignmentComponent,
    CommercialReleaseOverviewComponent,
    CommercialReleaseOverviewItemComponent,
    CommercialReleaseModalComponent,
    CommercialReleaseModalDetailsComponent,
    CommercialReleaseModalHistoryComponent,
    CommercialReleaseModalPartiesComponent,
    CommercialReleasePartyFieldComponent,
    CommercialReleaseUploadModalComponent
  ],
    imports: [RouterModule.forRoot(appRoutes, {enableTracing: false}),
        CookieModule.forRoot(),
        PortVisitCommonModule,
        BrowserModule,
        CommonModule,
        FormsModule,
        TypeaheadWindowComponent,
        PopoverComponent,
        HighlightComponent,
        NgOptimizedImage, StevedoreVisitOverviewServiceStatusComponent, FacetFilterSearchFieldComponent, MovementIconsComponent], providers: [
        AppHandler,
        ConsignmentHandler,
        provideHttpClient(withInterceptorsFromDi())
    ] })
export class PortVisitModule {
  constructor(injector: Injector) {
    InjectorProvider.injector = injector;
    InjectorProvider.injector.get(AppHandler);
    InjectorProvider.injector.get(ConsignmentHandler);
  }
}
